import React from 'react';
import { Box, Grid, Typography, Divider } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';
import { useAppSelector } from 'src/store';
import { StatusChangeAnalytics } from 'src/types/analytics';
import MetricCard from './MetricCard';
import AnalyticsChart from './AnalyticsChart';

interface StatusChangesSectionProps {
  loading: boolean;
}

const StatusChangesSection: React.FC<StatusChangesSectionProps> = ({ loading }) => {
  const statusChangeAnalytics = useAppSelector(state => state.dataAnalytics.statusChangeAnalytics);

  // Prepare data for the time-based chart
  const timeChartData = React.useMemo(() => {
    if (!statusChangeAnalytics?.timeBasedData) {
      return { labels: [], datasets: [] };
    }

    const sortedData = [...statusChangeAnalytics.timeBasedData].sort((a, b) =>
      new Date(a.period).getTime() - new Date(b.period).getTime()
    );

    return {
      labels: sortedData.map(item => item.period),
      datasets: [
        {
          label: 'Status Changes',
          data: sortedData.map(item => item.count),
          borderColor: '#9c27b0',
          backgroundColor: 'rgba(156, 39, 176, 0.2)',
          fill: true
        }
      ]
    };
  }, [statusChangeAnalytics]);

  // Prepare data for the status distribution chart
  const statusChartData = React.useMemo(() => {
    if (!statusChangeAnalytics?.statusDistribution) {
      return { labels: [], datasets: [] };
    }

    const entries = Object.entries(statusChangeAnalytics.statusDistribution);
    const labels = entries.map(([status]) =>
      status === 'needs_attention' ? 'Needs Attention' :
        status === 'closed' ? 'Closed' :
          status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    );

    return {
      labels,
      datasets: [
        {
          label: 'Status Distribution',
          data: entries.map(([_, count]) => count),
          backgroundColor: [
            '#ff9800',  // Needs Attention - orange
            '#4caf50',  // Closed - green
            '#2196f3'   // Other statuses - blue
          ]
        }
      ]
    };
  }, [statusChangeAnalytics]);

  // Prepare data for the reasons chart
  const reasonsChartData = React.useMemo(() => {
    if (!statusChangeAnalytics?.reasonBasedData) {
      return { labels: [], datasets: [] };
    }

    const sortedReasons = [...statusChangeAnalytics.reasonBasedData]
      .sort((a, b) => b.count - a.count);

    // Define a color palette for different types of reasons
    const colorMap = {
      'Maximum follow-ups reached': '#e91e63',     // Pink - automated closure
      'No response from contact': '#9c27b0',       // Purple - no engagement
      'No new information to add': '#3f51b5',      // Indigo - information related
      'Other': '#78909c',                         // Blue Grey - miscellaneous
      'User requested no contact': '#f44336',      // Red - user request
      'User requested call/human contact': '#ff9800', // Orange - escalation
      'Other needs attention': '#fdd835',          // Yellow - attention needed
      'Conversation completed': '#4caf50',         // Green - positive completion
      'Follow-ups disabled per policy': '#00bcd4', // Cyan - policy related
      'Quote needs attention': '#ff5722'           // Deep Orange - quote related
    };

    return {
      labels: sortedReasons.map(item => item.reason),
      datasets: [
        {
          label: 'Count',
          data: sortedReasons.map(item => item.count),
          backgroundColor: sortedReasons.map(item => colorMap[item.reason] || '#78909c'),
          borderColor: sortedReasons.map(item => colorMap[item.reason] || '#78909c'),
          borderWidth: 1,
          barThickness: 30,
          maxBarThickness: 35
        }
      ]
    };
  }, [statusChangeAnalytics]);

  // Calculate metrics for the cards using statusDistribution
  const needsAttentionCount = statusChangeAnalytics?.statusDistribution?.['needs_attention'] || 0;
  const closedCount = statusChangeAnalytics?.statusDistribution?.['closed'] || 0;

  // Get the most common reason
  const mostCommonReason = statusChangeAnalytics?.reasonBasedData?.[0]?.reason
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') || 'N/A';

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Status Changes Analytics
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Status Changes"
            value={statusChangeAnalytics?.totalCount || 0}
            icon={<SwapHorizIcon />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Needs Attention Changes"
            value={needsAttentionCount}
            icon={<WarningAmberIcon />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Closed Status Changes"
            value={closedCount}
            icon={<CheckCircleIcon />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Most Common Reason"
            value={mostCommonReason}
            icon={<ReportIcon />}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <AnalyticsChart
            title="Status Changes Over Time"
            data={timeChartData}
            type="line"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AnalyticsChart
            title="Status Distribution"
            data={statusChartData}
            type="pie"
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <AnalyticsChart
            title="Common Reasons for Status Changes"
            data={reasonsChartData}
            type="bar"
            loading={loading}
            height={400}
            options={{
              indexAxis: 'y',
              plugins: {
                legend: {
                  display: false
                }
              },
              scales: {
                x: {
                  grid: {
                    color: 'rgba(255, 255, 255, 0.1)'
                  }
                },
                y: {
                  grid: {
                    display: false
                  }
                }
              }
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default StatusChangesSection; 